import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ImgHTMLAttributes,
} from "react";

type Props = ImgHTMLAttributes<HTMLImageElement> & { fallback?: string };
/** Preserve caller geometry and upload bytes; never substitute a different product. */
export function BoundedImage(props: Props) {
  return <ImageRequest key={props.src} {...props} />;
}
function ImageRequest({ fallback = "Image unavailable", ...props }: Props) {
  const loading = props.loading ?? "lazy";
  const image = useRef<HTMLImageElement>(null);
  const terminal = useRef(false);
  const [failed, setFailed] = useState(!props.src);
  const [visible, setVisible] = useState(loading !== "lazy");
  const [loaded, setLoaded] = useState(false);
  const fail = useCallback(() => {
    terminal.current = true;
    setFailed(true);
  }, []);
  const complete = useCallback(
    (node: HTMLImageElement) => {
      if (terminal.current) return;
      if (node.naturalWidth <= 1 || node.naturalHeight <= 1) {
        fail();
        return;
      }
      const reveal = () => {
        if (!terminal.current && image.current === node) setLoaded(true);
      };
      if (node.decode)
        void node.decode().then(reveal, () => {
          if (image.current === node) fail();
        });
      else reveal();
    },
    [fail],
  );
  useEffect(() => {
    if (image.current?.complete) complete(image.current);
  }, [complete]);
  useEffect(() => {
    if (visible || !image.current) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(image.current);
    return () => observer.disconnect();
  }, [visible]);
  useEffect(() => {
    if (failed || loaded || !visible) return;
    const timer = window.setTimeout(fail, 3000);
    return () => window.clearTimeout(timer);
  }, [failed, loaded, visible, fail]);
  if (failed)
    return (
      <span
        role="img"
        aria-label={props.alt || fallback}
        className={props.className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background: "#eee",
          width: props.width,
          height: props.height,
          ...props.style,
        }}
      >
        {fallback}
      </span>
    );
  return (
    <img
      {...props}
      loading={loading}
      ref={image}
      decoding="async"
      onError={(event) => {
        fail();
        props.onError?.(event);
      }}
      onLoad={(event) => {
        complete(event.currentTarget);
        props.onLoad?.(event);
      }}
    />
  );
}
